/**
 * AdminGlobalBackButton
 *
 * A fixed floating pill in the top-left of every /admin/* subpage (except
 * the portal itself) that links back to /admin/portal. This is mounted
 * once at the app root so we don't have to touch every admin subpage's
 * JSX individually — one implementation guarantees consistency.
 *
 * Renders nothing outside the admin area or when the user isn't admin.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, LayoutGrid } from 'lucide-react';

// Routes that ARE the hub — we suppress the button here to avoid a
// self-referential link.
const HUB_ROUTES = new Set(['/admin', '/admin/portal', '/admin/']);

const AdminGlobalBackButton = ({ user }) => {
  const location = useLocation();
  const path = location.pathname;

  if (!user || user.role !== 'admin') return null;
  if (!path.startsWith('/admin')) return null;
  if (HUB_ROUTES.has(path)) return null;

  return (
    <div className="fixed top-3 left-4 z-[80] pointer-events-none">
      <Link
        to="/admin/portal"
        className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-background/90 backdrop-blur px-3 h-8 text-[11px] font-bold uppercase tracking-widest text-primary shadow-sm hover:bg-primary/10 transition-colors"
        data-testid="admin-global-back-btn"
        title="Return to Admin portal"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        <LayoutGrid className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Admin portal</span>
      </Link>
    </div>
  );
};

export default AdminGlobalBackButton;
